import AsyncStorage from '@react-native-async-storage/async-storage';
import { logEvent } from './analytics-service';
import { log } from './production-logger';


interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  cacheMs?: number;
  blockDurationMs?: number;
}

interface RequestRecord {
  timestamp: number;
  count: number;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresAt: number;
}

// Enhanced interfaces for authentication security
interface AuthAttempt {
  timestamp: number;
  identifier: string;
  operation: string;
  success: boolean;
  metadata?: Record<string, any>;
}

interface AuthRateLimitStatus {
  isBlocked: boolean;
  attemptsCount: number;
  maxAttempts: number;
  timeWindowMs: number;
  nextAttemptAllowedAt?: number;
  remainingTime?: number;
}

interface SessionData {
  userId: string;
  lastActivity: number;
  sessionStart: number;
  deviceId?: string;
}

// Storage keys for authentication rate limiting
const AUTH_RATE_LIMIT_KEY = '@InvoiceAI:authRateLimits';
const SESSION_TIMEOUT_KEY = '@InvoiceAI:sessionTimeout';
const BLOCKED_IDENTIFIERS_KEY = '@InvoiceAI:blockedIdentifiers';

class RateLimitingService {
  private requests: Map<string, RequestRecord[]> = new Map();
  private cache: Map<string, CacheEntry> = new Map();

  // Default rate limiting configs for different operations
  private readonly configs: Record<string, RateLimitConfig> = {
    // API Rate Limiting (existing)
    dashboard: { maxRequests: 5, windowMs: 60000, cacheMs: 30000 }, // 5 req/min, cache 30s
    statistics: { maxRequests: 3, windowMs: 60000, cacheMs: 60000 }, // 3 req/min, cache 1min
    invoices: { maxRequests: 10, windowMs: 60000, cacheMs: 15000 }, // 10 req/min, cache 15s
    suppliers: { maxRequests: 8, windowMs: 60000, cacheMs: 30000 }, // 8 req/min, cache 30s
    charts: { maxRequests: 2, windowMs: 60000, cacheMs: 120000 }, // 2 req/min, cache 2min
    default: { maxRequests: 5, windowMs: 60000, cacheMs: 30000 },
    
    // Authentication Security Rate Limiting (new)
    login: { maxRequests: 5, windowMs: 15 * 60 * 1000, blockDurationMs: 30 * 60 * 1000 }, // 5 attempts per 15min, block 30min
    password_reset: { maxRequests: 3, windowMs: 60 * 60 * 1000, blockDurationMs: 2 * 60 * 60 * 1000 }, // 3 attempts per hour, block 2h
    otp_verification: { maxRequests: 5, windowMs: 10 * 60 * 1000, blockDurationMs: 60 * 60 * 1000 }, // 5 attempts per 10min, block 1h
    biometric_auth: { maxRequests: 10, windowMs: 5 * 60 * 1000, blockDurationMs: 15 * 60 * 1000 }, // 10 attempts per 5min, block 15min
    registration: { maxRequests: 3, windowMs: 60 * 60 * 1000, blockDurationMs: 60 * 60 * 1000 }, // 3 attempts per hour, block 1h
  };

  // Session timeout configuration
  private sessionConfig = {
    inactivityTimeoutMs: 30 * 60 * 1000, // 30 minutes of inactivity
    maxSessionDurationMs: 8 * 60 * 60 * 1000, // 8 hours maximum session
    warningBeforeTimeoutMs: 5 * 60 * 1000, // 5 minutes warning
  };

  private cleanupInterval?: NodeJS.Timeout;

  /**
   * Check if a request is allowed based on rate limiting rules
   * @param key - Unique identifier for the request type
   * @param config - Optional custom rate limit config
   * @returns Promise<boolean> - Whether the request is allowed
   */
  async isRequestAllowed(key: string, config?: RateLimitConfig): Promise<boolean> {
    const rateLimitConfig = config || this.configs[key] || this.configs.default;
    const now = Date.now();
    const windowStart = now - rateLimitConfig.windowMs;

    // Get or initialize request records for this key
    let records = this.requests.get(key) || [];

    // Filter out old records outside the time window
    records = records.filter(record => record.timestamp > windowStart);

    // Calculate total requests in the current window
    const totalRequests = records.reduce((sum, record) => sum + record.count, 0);

    if (totalRequests >= rateLimitConfig.maxRequests) {
      log.warn(
        `Rate limit exceeded for ${key}: ${totalRequests}/${rateLimitConfig.maxRequests}`,
      );
      return false;
    }

    // Add current request
    records.push({ timestamp: now, count: 1 });
    this.requests.set(key, records);

    return true;
  }

  /**
   * Get cached data if available and not expired
   * @param key - Cache key
   * @returns Cached data or null
   */
  getCachedData(key: string): any | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    const now = Date.now();
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    log.debug(`Cache hit for ${key}`);
    return entry.data;
  }

  /**
   * Cache data with expiration
   * @param key - Cache key
   * @param data - Data to cache
   * @param cacheMs - Cache duration in milliseconds
   */
  setCachedData(key: string, data: any, cacheMs?: number): void {
    const now = Date.now();
    const defaultCacheMs = this.configs.default.cacheMs || 30000;
    const expiresAt = now + (cacheMs || defaultCacheMs);

    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt,
    });

    log.debug(`Data cached for ${key}, expires in ${(expiresAt - now) / 1000}s`);
  }

  /**
   * Execute a function with rate limiting and caching
   * @param key - Unique identifier for this operation
   * @param operation - Function to execute
   * @param options - Rate limiting and caching options
   * @returns Promise with the result
   */
  async withRateLimit<T>(
    key: string,
    operation: () => Promise<T>,
    options?: {
      rateLimitConfig?: RateLimitConfig;
      cacheKey?: string;
      skipCache?: boolean;
      onRateLimited?: () => Promise<T> | T;
    },
  ): Promise<T> {
    const cacheKey = options?.cacheKey || key;
    const config = options?.rateLimitConfig || this.configs[key] || this.configs.default;

    // Check cache first (unless explicitly skipped)
    if (!options?.skipCache) {
      const cachedData = this.getCachedData(cacheKey);
      if (cachedData !== null) {
        return cachedData;
      }
    }

    // Check rate limiting
    const isAllowed = await this.isRequestAllowed(key, config);

    if (!isAllowed) {
      if (options?.onRateLimited) {
        log.debug(`Rate limited ${key}, using fallback`);
        return await options.onRateLimited();
      }

      // Try to return stale cache data if available
      const staleData = this.cache.get(cacheKey);
      if (staleData) {
        log.debug(`Rate limited ${key}, returning stale cache`);
        return staleData.data;
      }

      throw new Error(`Rate limit exceeded for ${key} and no fallback available`);
    }

    try {
      // Execute the operation
      log.debug(`Executing operation ${key}`);
      const result = await operation();

      // Cache the result
      if (config.cacheMs && !options?.skipCache) {
        this.setCachedData(cacheKey, result, config.cacheMs);
      }

      return result;
    } catch (error) {
      log.error(`Operation ${key} failed:`, error);

      // Try to return stale cache data on error
      const staleData = this.cache.get(cacheKey);
      if (staleData) {
        log.debug(`Operation failed for ${key}, returning stale cache`);
        return staleData.data;
      }

      throw error;
    }
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
    log.debug('All cache cleared');
  }

  /**
   * Clear cache for a specific key
   * @param key - Cache key to clear
   */
  clearCacheForKey(key: string): void {
    this.cache.delete(key);
    log.debug(`Cache cleared for ${key}`);
  }

  /**
   * Get cache statistics
   * @returns Object with cache stats
   */
  getCacheStats(): {
    totalEntries: number;
    totalSize: number;
    oldestEntry: number | null;
    newestEntry: number | null;
  } {
    const entries = Array.from(this.cache.values());
    const now = Date.now();

    return {
      totalEntries: entries.length,
      totalSize: JSON.stringify(Array.from(this.cache.entries())).length,
      oldestEntry: entries.length > 0 ? Math.min(...entries.map(e => e.timestamp)) : null,
      newestEntry: entries.length > 0 ? Math.max(...entries.map(e => e.timestamp)) : null,
    };
  }

  // ========== AUTHENTICATION SECURITY METHODS ==========

  /**
   * Check if an authentication operation is rate limited
   */
  async checkAuthRateLimit(
    operation: string,
    identifier: string
  ): Promise<AuthRateLimitStatus> {
    try {
      const config = this.configs[operation];
      if (!config || !config.blockDurationMs) {
        return {
          isBlocked: false,
          attemptsCount: 0,
          maxAttempts: config?.maxRequests || Infinity,
          timeWindowMs: config?.windowMs || 0,
        };
      }

      const key = this.generateAuthKey(operation, identifier);
      const attempts = await this.getAuthAttempts(key);
      
      // Check if currently blocked
      const blockedUntil = await this.getBlockedUntil(key);
      if (blockedUntil && blockedUntil > Date.now()) {
        const remainingTime = blockedUntil - Date.now();
        return {
          isBlocked: true,
          attemptsCount: attempts.length,
          maxAttempts: config.maxRequests,
          timeWindowMs: config.windowMs,
          nextAttemptAllowedAt: blockedUntil,
          remainingTime,
        };
      }

      // Count recent failed attempts
      const windowStart = Date.now() - config.windowMs;
      const recentFailedAttempts = attempts.filter(
        attempt => attempt.timestamp > windowStart && !attempt.success
      );

      const isBlocked = recentFailedAttempts.length >= config.maxRequests;

      return {
        isBlocked,
        attemptsCount: recentFailedAttempts.length,
        maxAttempts: config.maxRequests,
        timeWindowMs: config.windowMs,
        ...(isBlocked && {
          nextAttemptAllowedAt: Date.now() + config.blockDurationMs,
          remainingTime: config.blockDurationMs,
        }),
      };
    } catch (error) {
      log.error('Error checking auth rate limit:', error);
      // Fail open for security checks
      return {
        isBlocked: false,
        attemptsCount: 0,
        maxAttempts: Infinity,
        timeWindowMs: 0,
      };
    }
  }

  /**
   * Record an authentication attempt
   */
  async recordAuthAttempt(
    operation: string,
    identifier: string,
    success: boolean,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const config = this.configs[operation];
      if (!config || !config.blockDurationMs) return;

      const key = this.generateAuthKey(operation, identifier);
      const attempt: AuthAttempt = {
        timestamp: Date.now(),
        identifier: this.hashIdentifier(identifier),
        operation,
        success,
        metadata,
      };

      // Get existing attempts
      const attempts = await this.getAuthAttempts(key);
      attempts.push(attempt);

      // Clean old attempts
      const windowStart = Date.now() - config.windowMs;
      const cleanAttempts = attempts.filter(a => a.timestamp > windowStart);

      // Save attempts
      await this.saveAuthAttempts(key, cleanAttempts);

      // Check if we need to block
      const failedAttempts = cleanAttempts.filter(a => !a.success);
      if (failedAttempts.length >= config.maxRequests) {
        const blockUntil = Date.now() + config.blockDurationMs;
        await this.setBlocked(key, blockUntil);
        
        // Log security event
        logEvent('auth_rate_limit_triggered', {
          operation,
          attempts: failedAttempts.length,
          blockDuration: config.blockDurationMs,
        });

        log.warn(`Blocked ${operation} for identifier due to ${failedAttempts.length} failed attempts`);
      }
    } catch (error) {
      log.error('Error recording auth attempt:', error);
    }
  }

  /**
   * Reset authentication rate limit for an identifier
   */
  async resetAuthRateLimit(operation: string, identifier: string): Promise<void> {
    try {
      const key = this.generateAuthKey(operation, identifier);
      await AsyncStorage.removeItem(`${AUTH_RATE_LIMIT_KEY}:${key}`);
      await AsyncStorage.removeItem(`${BLOCKED_IDENTIFIERS_KEY}:${key}`);
    } catch (error) {
      log.error('Error resetting auth rate limit:', error);
    }
  }

  // ========== SESSION TIMEOUT METHODS ==========

  /**
   * Record user activity to reset inactivity timer
   */
  async recordActivity(userId: string, deviceId?: string): Promise<void> {
    try {
      const sessionData: SessionData = {
        userId,
        lastActivity: Date.now(),
        sessionStart: Date.now(),
        deviceId,
      };

      // Try to get existing session to preserve start time
      const existingDataJson = await AsyncStorage.getItem(`${SESSION_TIMEOUT_KEY}:${userId}`);
      if (existingDataJson) {
        const existingData = JSON.parse(existingDataJson) as SessionData;
        sessionData.sessionStart = existingData.sessionStart;
      }

      await AsyncStorage.setItem(
        `${SESSION_TIMEOUT_KEY}:${userId}`,
        JSON.stringify(sessionData)
      );
    } catch (error) {
      log.error('Error recording activity:', error);
    }
  }

  /**
   * Check if session should timeout
   */
  async checkSessionTimeout(userId: string): Promise<{
    shouldTimeout: boolean;
    shouldWarn: boolean;
    remainingTime: number;
    reason?: 'inactivity' | 'max_duration';
  }> {
    try {
      const sessionDataJson = await AsyncStorage.getItem(`${SESSION_TIMEOUT_KEY}:${userId}`);
      if (!sessionDataJson) {
        return {
          shouldTimeout: true,
          shouldWarn: false,
          remainingTime: 0,
          reason: 'inactivity',
        };
      }

      const sessionData = JSON.parse(sessionDataJson) as SessionData;
      const now = Date.now();

      // Check maximum session duration
      const sessionDuration = now - sessionData.sessionStart;
      if (sessionDuration > this.sessionConfig.maxSessionDurationMs) {
        return {
          shouldTimeout: true,
          shouldWarn: false,
          remainingTime: 0,
          reason: 'max_duration',
        };
      }

      // Check inactivity timeout
      const inactiveTime = now - sessionData.lastActivity;
      const timeUntilInactivityTimeout = this.sessionConfig.inactivityTimeoutMs - inactiveTime;
      
      if (timeUntilInactivityTimeout <= 0) {
        return {
          shouldTimeout: true,
          shouldWarn: false,
          remainingTime: 0,
          reason: 'inactivity',
        };
      }

      const shouldWarn = timeUntilInactivityTimeout <= this.sessionConfig.warningBeforeTimeoutMs;

      return {
        shouldTimeout: false,
        shouldWarn,
        remainingTime: timeUntilInactivityTimeout,
      };
    } catch (error) {
      log.error('Error checking session timeout:', error);
      return {
        shouldTimeout: false,
        shouldWarn: false,
        remainingTime: this.sessionConfig.inactivityTimeoutMs,
      };
    }
  }

  /**
   * Clear session data for a user
   */
  async clearSession(userId: string): Promise<void> {
    try {
      await AsyncStorage.removeItem(`${SESSION_TIMEOUT_KEY}:${userId}`);
    } catch (error) {
      log.error('Error clearing session:', error);
    }
  }

  /**
   * Update session configuration
   */
  updateSessionConfig(config: Partial<typeof this.sessionConfig>): void {
    this.sessionConfig = { ...this.sessionConfig, ...config };
  }

  // ========== HELPER METHODS ==========

  private generateAuthKey(operation: string, identifier: string): string {
    return `${operation}:${this.hashIdentifier(identifier)}`;
  }

  private hashIdentifier(identifier: string): string {
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      const char = identifier.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private async getAuthAttempts(key: string): Promise<AuthAttempt[]> {
    try {
      const attemptsJson = await AsyncStorage.getItem(`${AUTH_RATE_LIMIT_KEY}:${key}`);
      return attemptsJson ? JSON.parse(attemptsJson) : [];
    } catch (error) {
      log.error('Error getting auth attempts:', error);
      return [];
    }
  }

  private async saveAuthAttempts(key: string, attempts: AuthAttempt[]): Promise<void> {
    try {
      await AsyncStorage.setItem(`${AUTH_RATE_LIMIT_KEY}:${key}`, JSON.stringify(attempts));
    } catch (error) {
      log.error('Error saving auth attempts:', error);
    }
  }

  private async getBlockedUntil(key: string): Promise<number | null> {
    try {
      const blockedUntilStr = await AsyncStorage.getItem(`${BLOCKED_IDENTIFIERS_KEY}:${key}`);
      return blockedUntilStr ? parseInt(blockedUntilStr, 10) : null;
    } catch (error) {
      log.error('Error getting blocked until:', error);
      return null;
    }
  }

  private async setBlocked(key: string, until: number): Promise<void> {
    try {
      await AsyncStorage.setItem(`${BLOCKED_IDENTIFIERS_KEY}:${key}`, until.toString());
    } catch (error) {
      log.error('Error setting blocked:', error);
    }
  }

  /**
   * Format remaining time for user display
   */
  formatRemainingTime(remainingTimeMs: number): string {
    const minutes = Math.ceil(remainingTimeMs / (60 * 1000));
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 
        ? `${hours}h ${remainingMinutes}m`
        : `${hours}h`;
    }
    
    return `${minutes}m`;
  }

  /**
   * Clean up expired cache entries and old request records
   */
  cleanup(): void {
    const now = Date.now();

    // Clean expired cache entries
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
      }
    }

    // Clean old request records (keep only last hour)
    const oneHourAgo = now - 3600000;
    for (const [key, records] of this.requests.entries()) {
      const cleanRecords = records.filter(record => record.timestamp > oneHourAgo);
      if (cleanRecords.length === 0) {
        this.requests.delete(key);
      } else {
        this.requests.set(key, cleanRecords);
      }
    }

    // Clean expired authentication data asynchronously
    this.cleanupAuthData().catch(error => {
      log.error('Error during auth data cleanup:', error);
    });

    log.debug('Rate limiting service cleanup completed');
  }

  /**
   * Clean up expired authentication data
   */
  private async cleanupAuthData(): Promise<void> {
    try {
      const now = Date.now();
      
      // Get all stored keys
      const allKeys = await AsyncStorage.getAllKeys();
      
      // Clean expired blocked identifiers
      const blockedKeys = allKeys.filter(key => key.startsWith(BLOCKED_IDENTIFIERS_KEY));
      for (const key of blockedKeys) {
        try {
          const blockedUntilStr = await AsyncStorage.getItem(key);
          if (blockedUntilStr) {
            const blockedUntil = parseInt(blockedUntilStr, 10);
            if (blockedUntil < now) {
              await AsyncStorage.removeItem(key);
            }
          }
        } catch (error) {
          log.error(`Error cleaning blocked key ${key}:`, error);
        }
      }

      // Clean old auth attempts (keep only last 24 hours)
      const dayAgo = now - 24 * 60 * 60 * 1000;
      const authKeys = allKeys.filter(key => key.startsWith(AUTH_RATE_LIMIT_KEY));
      for (const key of authKeys) {
        try {
          const attemptsJson = await AsyncStorage.getItem(key);
          if (attemptsJson) {
            const attempts = JSON.parse(attemptsJson) as AuthAttempt[];
            const validAttempts = attempts.filter(attempt => attempt.timestamp > dayAgo);
            
            if (validAttempts.length === 0) {
              await AsyncStorage.removeItem(key);
            } else if (validAttempts.length < attempts.length) {
              await AsyncStorage.setItem(key, JSON.stringify(validAttempts));
            }
          }
        } catch (error) {
          log.error(`Error cleaning auth attempts key ${key}:`, error);
        }
      }

      // Clean old session data (beyond max session duration + 1 day)
      const maxAge = this.sessionConfig.maxSessionDurationMs + 24 * 60 * 60 * 1000;
      const sessionKeys = allKeys.filter(key => key.startsWith(SESSION_TIMEOUT_KEY));
      for (const key of sessionKeys) {
        try {
          const sessionDataJson = await AsyncStorage.getItem(key);
          if (sessionDataJson) {
            const sessionData = JSON.parse(sessionDataJson) as SessionData;
            const sessionAge = now - sessionData.sessionStart;
            if (sessionAge > maxAge) {
              await AsyncStorage.removeItem(key);
            }
          }
        } catch (error) {
          log.error(`Error cleaning session key ${key}:`, error);
        }
      }

      log.debug('Authentication data cleanup completed');
    } catch (error) {
      log.error('Error during authentication data cleanup:', error);
    }
  }

  /**
   * Initialize the service with periodic cleanup
   */
  initialize(): void {
    // Clean up every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 300000);

    // Run initial cleanup
    this.cleanup();

    log.debug('Rate limiting service initialized with authentication security features');
  }

  /**
   * Destroy the service and clean up resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }
    log.debug('Rate limiting service destroyed');
  }

  /**
   * Reset rate limiting for a specific key (useful for testing)
   * @param key - Key to reset
   */
  resetRateLimit(key: string): void {
    this.requests.delete(key);
    log.debug(`Rate limit reset for ${key}`);
  }
}

const rateLimitingService = new RateLimitingService();
export default rateLimitingService;
